import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiDollarSign, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import api from '../../api';

// 시/도 목록
const PROVINCES = [
  '서울특별시', '부산광역시', '대구광역시', '인천광역시', '광주광역시',
  '대전광역시', '울산광역시', '세종특별자치시', '경기도', '강원도',
  '충청북도', '충청남도', '전라북도', '전라남도', '경상북도', '경상남도', '제주특별자치도'
];

export default function SuperTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState({
    tenantId: '',
    name: '',
    plan: 'free',
    region: { province: '', city: '' },
    operationType: 'solo',
    lessonReportConfig: { targetType: 'class', frequency: 'daily' },
    adminEmail: '',
    adminPassword: '',
    adminName: ''
  });
  const [chargeData, setChargeData] = useState({
    amount: '',
    reason: ''
  });

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const { data } = await api.get('/api/super/tenants', { params });
      setTenants(data.tenants || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openModal = (tenant = null) => {
    if (tenant) {
      setEditingTenant(tenant);
      setFormData({
        tenantId: tenant.tenantId,
        name: tenant.name,
        plan: tenant.plan?.type || 'free',
        region: tenant.region || { province: '', city: '' },
        operationType: tenant.operationType || 'solo',
        lessonReportConfig: tenant.lessonReportConfig || { targetType: 'class', frequency: 'daily' },
        adminEmail: '',
        adminPassword: '',
        adminName: ''
      });
    } else {
      setEditingTenant(null);
      setFormData({
        tenantId: '',
        name: '',
        plan: 'free',
        region: { province: '', city: '' },
        operationType: 'solo',
        lessonReportConfig: { targetType: 'class', frequency: 'daily' },
        adminEmail: '',
        adminPassword: '',
        adminName: '관리자'
      });
    }
    setShowModal(true);
  };

  const openChargeModal = (tenant) => {
    setSelectedTenant(tenant);
    setChargeData({ amount: '', reason: '' });
    setShowChargeModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit - editingTenant:', editingTenant);
    console.log('handleSubmit - formData:', formData);
    try {
      if (editingTenant) {
        await api.put(`/api/super/tenants/${editingTenant._id}`, {
          name: formData.name,
          plan: formData.plan,
          region: formData.region,
          operationType: formData.operationType,
          lessonReportConfig: formData.lessonReportConfig
        });
      } else {
        await api.post('/api/super/tenants', formData);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  const handleToggleStatus = async (tenant) => {
    try {
      await api.put(`/api/super/tenants/${tenant._id}/toggle-status`);
      loadData();
    } catch (error) {
      alert('상태 변경에 실패했습니다');
    }
  };

  const handleCharge = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/super/tenants/${selectedTenant.tenantId}/charge`, chargeData);
      alert('충전이 완료되었습니다');
      setShowChargeModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '충전에 실패했습니다');
    }
  };

  const handleDelete = async (tenant) => {
    const confirmMsg = `정말로 "${tenant.name}" 테넌트를 완전히 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없습니다!\n- 모든 사용자 계정\n- 포인트 및 충전 내역\n- 알림 내역\n\n모든 데이터가 영구 삭제됩니다.`;
    if (!window.confirm(confirmMsg)) return;

    // 2차 확인
    const tenantIdConfirm = window.prompt(`삭제를 확인하려면 테넌트 ID "${tenant.tenantId}"를 입력하세요:`);
    if (tenantIdConfirm !== tenant.tenantId) {
      alert('테넌트 ID가 일치하지 않아 삭제가 취소되었습니다.');
      return;
    }

    try {
      await api.delete(`/api/super/tenants/${tenant._id}`);
      alert('테넌트가 완전히 삭제되었습니다.');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '삭제에 실패했습니다');
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num || 0);
  };

  const getPlanBadge = (plan) => {
    const badges = {
      free: { bg: '#E5E7EB', color: '#374151', text: 'Free' },
      basic: { bg: '#DBEAFE', color: '#1D4ED8', text: 'Basic' },
      pro: { bg: '#FEF3C7', color: '#92400E', text: 'Pro' }
    };
    const b = badges[plan] || badges.free;
    return <span className="badge" style={{ background: b.bg, color: b.color }}>{b.text}</span>;
  };

  return (
    <div className="super-tenants-page">
      <div className="page-header">
        <h2>테넌트 관리</h2>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> 테넌트 생성
        </button>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="search-input">
          <FiSearch />
          <input
            type="text"
            placeholder="테넌트 ID 또는 이름 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">전체 상태</option>
          <option value="active">활성</option>
          <option value="inactive">비활성</option>
        </select>
      </div>

      {/* Tenants Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>테넌트 ID</th>
              <th>학원명</th>
              <th>플랜</th>
              <th>사용자</th>
              <th>포인트</th>
              <th>상태</th>
              <th>가입일</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="loading">로딩 중...</td></tr>
            ) : tenants.length === 0 ? (
              <tr><td colSpan="8" className="empty">테넌트가 없습니다</td></tr>
            ) : (
              tenants.map((tenant) => (
                <tr key={tenant._id}>
                  <td><code>{tenant.tenantId}</code></td>
                  <td>{tenant.name}</td>
                  <td>{getPlanBadge(tenant.plan?.type)}</td>
                  <td>{tenant.userCount || 0}명</td>
                  <td>{formatNumber(tenant.pointBalance)}P</td>
                  <td>
                    <span className={`status ${tenant.isActive ? 'active' : 'inactive'}`}>
                      {tenant.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td>{format(new Date(tenant.createdAt), 'yyyy-MM-dd')}</td>
                  <td>
                    <button className="icon-btn" onClick={() => openChargeModal(tenant)} title="포인트 충전">
                      <FiDollarSign />
                    </button>
                    <button className="icon-btn" onClick={() => openModal(tenant)} title="수정">
                      <FiEdit2 />
                    </button>
                    <button
                      className={`icon-btn ${tenant.isActive ? '' : 'success'}`}
                      onClick={() => handleToggleStatus(tenant)}
                      title={tenant.isActive ? '비활성화' : '활성화'}
                    >
                      {tenant.isActive ? <FiToggleRight /> : <FiToggleLeft />}
                    </button>
                    <button className="icon-btn danger" onClick={() => handleDelete(tenant)} title="완전 삭제">
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTenant ? '테넌트 수정' : '테넌트 생성'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>테넌트 ID *</label>
                <input
                  type="text"
                  value={formData.tenantId}
                  onChange={(e) => setFormData({ ...formData, tenantId: e.target.value.toLowerCase() })}
                  placeholder="academy1"
                  pattern="[a-z0-9]+"
                  disabled={!!editingTenant}
                  autoComplete="off"
                  required
                />
                {!editingTenant && <small className="text-muted">영문 소문자, 숫자만 가능</small>}
              </div>
              <div className="form-group">
                <label>학원명 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="OO학원"
                  required
                />
              </div>
              <div className="form-group">
                <label>플랜</label>
                <select
                  value={formData.plan}
                  onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                >
                  <option value="free">Free (학생 50명)</option>
                  <option value="basic">Basic (무제한, 월 10,000P)</option>
                  <option value="pro">Pro (무제한, 알림톡 700건)</option>
                </select>
              </div>

              <hr />
              <h4>운영 정보</h4>

              <div className="form-group">
                <label>활동 지역</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    value={formData.region?.province || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      region: { ...formData.region, province: e.target.value }
                    })}
                    style={{ flex: 1 }}
                  >
                    <option value="">시/도 선택</option>
                    {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input
                    type="text"
                    value={formData.region?.city || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      region: { ...formData.region, city: e.target.value }
                    })}
                    placeholder="시/군/구"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>운영 형태</label>
                <select
                  value={formData.operationType}
                  onChange={(e) => setFormData({ ...formData, operationType: e.target.value })}
                >
                  <option value="solo">1인 운영</option>
                  <option value="team">팀 운영</option>
                </select>
              </div>

              <div className="form-group">
                <label>수업일지 유형</label>
                <select
                  value={formData.lessonReportConfig?.targetType || 'class'}
                  onChange={(e) => setFormData({
                    ...formData,
                    lessonReportConfig: { ...formData.lessonReportConfig, targetType: e.target.value }
                  })}
                >
                  <option value="class">반별 수업일지</option>
                  <option value="student">학생별 수업일지 (개별맞춤)</option>
                </select>
              </div>

              <div className="form-group">
                <label>수업일지 주기</label>
                <select
                  value={formData.lessonReportConfig?.frequency || 'daily'}
                  onChange={(e) => setFormData({
                    ...formData,
                    lessonReportConfig: { ...formData.lessonReportConfig, frequency: e.target.value }
                  })}
                >
                  <option value="daily">매 수업마다</option>
                  <option value="weekly">주간 리포트</option>
                </select>
              </div>

              {!editingTenant && (
                <>
                  <hr />
                  <h4>관리자 계정</h4>
                  <div className="form-group">
                    <label>관리자 이메일 *</label>
                    <input
                      type="email"
                      value={formData.adminEmail}
                      onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                      placeholder="admin@academy.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>관리자 비밀번호 *</label>
                    <input
                      type="password"
                      value={formData.adminPassword}
                      onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                      placeholder="6자 이상"
                      minLength={6}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>관리자 이름</label>
                    <input
                      type="text"
                      value={formData.adminName}
                      onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                      placeholder="관리자"
                    />
                  </div>
                </>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTenant ? '수정' : '생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Charge Modal */}
      {showChargeModal && selectedTenant && (
        <div className="modal-overlay" onClick={() => setShowChargeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>포인트 직접 충전</h3>
              <button className="close-btn" onClick={() => setShowChargeModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleCharge}>
              <div className="form-group">
                <label>테넌트</label>
                <input type="text" value={`${selectedTenant.name} (${selectedTenant.tenantId})`} disabled />
              </div>
              <div className="form-group">
                <label>현재 잔액</label>
                <input type="text" value={`${formatNumber(selectedTenant.pointBalance)} P`} disabled />
              </div>
              <div className="form-group">
                <label>충전 금액 *</label>
                <input
                  type="number"
                  value={chargeData.amount}
                  onChange={(e) => setChargeData({ ...chargeData, amount: e.target.value })}
                  placeholder="10000"
                  min="1"
                  required
                />
              </div>
              <div className="form-group">
                <label>사유</label>
                <input
                  type="text"
                  value={chargeData.reason}
                  onChange={(e) => setChargeData({ ...chargeData, reason: e.target.value })}
                  placeholder="보너스 충전, 이벤트 등"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowChargeModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiDollarSign /> 충전
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
